// Изо-линии высоты волны: контуры равной Hs поверх карты. Геометрию считает
// браузер (marchingSquares) по сетке волнения на вьюпорт. Линия — единым красным
// цветом (отличается от жёлтых термических фронтов); высоту читаем не по цвету, а
// по числовой подписи вдоль линии («2 м»). Белая подложка (как у изотерм) — чтобы
// линия читалась и на «медленной» (тёмной) части шкалы течений.

import { mapColorOf } from '../brand/mapColors'
import { OVERLAY_PREFIX } from './layerOrder'
import { contourAtLevel } from '../maps/marchingSquares'
import type { SstGrid } from '../maps/marchingSquares'

export const WAVE_ISO_PREFIX = OVERLAY_PREFIX + 'wave-iso-'

export type WaveIsoPart = 'line' | 'casing' | 'labels'
export function waveIsoLayerId(key: string, part: WaveIsoPart): string {
  return `${WAVE_ISO_PREFIX}${key}-${part}`
}
export function waveIsoSourceId(key: string): string {
  return `${WAVE_ISO_PREFIX}src-${key}`
}
export function waveIsoLabelSourceId(key: string): string {
  return `${WAVE_ISO_PREFIX}lbl-src-${key}`
}

/** Префикс id картинки-подписи уровня — под data-driven icon-image
 *  (`['concat', WAVE_ISO_LABEL_PREFIX, ['to-string', ['get','level']]]`). */
export const WAVE_ISO_LABEL_PREFIX = 'wave-lbl-'
export function waveIsoLabelImageId(level: number): string {
  return `${WAVE_ISO_LABEL_PREFIX}${level}`
}

/** Уровни изолиний высоты волны, м — дефолтные (fallback), когда шаг не задан. */
export const WAVE_ISO_LEVELS = [1, 2, 3, 4, 5, 6]

// Кастомный шаг изолиний (метры): дефолт 0.5, ползунок 0.2–7.
export const WAVE_STEP_MIN = 0.2
export const WAVE_STEP_MAX = 7
export const WAVE_STEP_DEFAULT = 0.5
/** Шаг ползунка шага (десятые метра) — все уровни кратны 0.1 → ≤1 знака после запятой. */
export const WAVE_STEP_SLIDER_STEP = 0.1

/** Наибольшая Hs в сетке (для верхней границы набора уровней); 0, если данных нет. */
export function gridMaxHs(grid: SstGrid): number {
  let max = 0
  for (const row of grid.values) for (const v of row) if (v != null && v > max) max = v
  return max
}

/** Уровни изолиний = кратные шагу от step до maxHs включительно (пик подписан).
 *  Пустой массив, если море спокойнее шага. Округление до 0.01 — чтобы 3·0.2 не
 *  давало 0.6000004. Предохранитель 200 уровней — от вырожденно малого шага. */
export function waveLevelsForStep(step: number, maxHs: number): number[] {
  if (!(step > 0) || !(maxHs > 0)) return []
  const levels: number[] = []
  for (let k = 1; k * step <= maxHs + 1e-9; k++) {
    levels.push(Math.round(k * step * 100) / 100)
    if (levels.length >= 200) break
  }
  return levels
}

/** Текст подписи уровня, RU-формат: «0,5» / «1» / «1,5» (без хвостовых нулей). */
export function waveLabelText(level: number): string {
  return String(level).replace('.', ',')
}

/** Единый цвет изолиний волнения (и их подписей) — гаммы домена. У нас он
 *  красный, и поэтому термические фронты жёлтые: линии не путаются на карте. */
export function waveIsoColor(): string {
  return mapColorOf('wave')
}

/** Строит коллекцию контуров по сетке волнения: по фиче на уровень, с цветом уровня.
 *  Пустые (уровень выше/ниже поля) остаются пустой геометрией — рисуют пусто. */
export function buildWaveContours(
  grid: SstGrid,
  levels: number[] = WAVE_ISO_LEVELS,
): GeoJSON.FeatureCollection {
  const features = levels.map(level => {
    const f = contourAtLevel(grid, level) as GeoJSON.Feature
    return {
      ...f,
      properties: { ...(f.properties ?? {}), level, color: waveIsoColor() },
    }
  })
  return { type: 'FeatureCollection', features }
}

export type WaveLabelProps = { level: number; color: string }

/** Точки-подписи высоты вдоль изолиний: «2 м» повторяется вдоль линии, как подпись
 *  температуры у фронтов. Высота ПОСТОЯННА вдоль изолинии → подпись = сам уровень.
 *  Разреживаем по бакетам ~minSep×minSep (иначе подпись на каждую клетку сетки);
 *  MapLibre доразредит по налеганию под текущий зум (`icon-allow-overlap:false`). */
export function buildWaveLabels(
  grid: SstGrid,
  levels: number[] = WAVE_ISO_LEVELS,
): GeoJSON.FeatureCollection<GeoJSON.Point, WaveLabelProps> {
  // Гуще шага сетки: подпись примерно раз в несколько десятых градуса.
  const minSep = Math.max(grid.step * 6, 0.3)
  const features: GeoJSON.Feature<GeoJSON.Point, WaveLabelProps>[] = []
  for (const level of levels) {
    const color = waveIsoColor()
    const seen = new Set<string>()
    const contour = contourAtLevel(grid, level)
    for (const [a, b] of contour.geometry.coordinates) {
      const lon = (a[0] + b[0]) / 2
      const lat = (a[1] + b[1]) / 2
      const bucket = `${Math.round(lon / minSep)},${Math.round(lat / minSep)}`
      if (seen.has(bucket)) continue
      seen.add(bucket)
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lon, lat] },
        properties: { level, color },
      })
    }
  }
  return { type: 'FeatureCollection', features }
}
